import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Download as DownloadIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { buildHarvestLocationsEndpoint } from '../utils/harvestLocations';

function HarvestContacts({ organizationSlug, refreshTrigger }) {
  const [contacts, setContacts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    location_id: '',
    date_from: null,
    date_to: null
  });
  const [editDialog, setEditDialog] = useState({ open: false, contact: null });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  useEffect(() => {
    fetchLocations();
  }, [organizationSlug]);

  useEffect(() => {
    loadContacts();
  }, [refreshTrigger, page, rowsPerPage, filters]);

  const fetchLocations = async () => {
    if (!organizationSlug) return;

    try {
      const response = await axios.get(buildHarvestLocationsEndpoint(organizationSlug), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const loadContacts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage
      });

      if (filters.search) params.append('search', filters.search);
      if (filters.location_id) params.append('location_id', filters.location_id);
      if (filters.date_from) params.append('date_from', filters.date_from.toISOString().split('T')[0]);
      if (filters.date_to) params.append('date_to', filters.date_to.toISOString().split('T')[0]);

      const response = await axios.get(`/api/harvest/contacts?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setContacts(response.data.contacts);
      setTotal(response.data.pagination.total);
    } catch (error) {
      console.error('Error loading contacts:', error);
      setMessage({ type: 'error', text: 'Failed to load contacts' });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filters change
  };

  const handleEdit = (contact) => {
    setEditDialog({ open: true, contact: { ...contact } });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    try {
      await axios.delete(`/api/harvest/contacts/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setMessage({ type: 'success', text: 'Contact deleted successfully' });
      loadContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      setMessage({ type: 'error', text: 'Failed to delete contact' });
    }
  };

  const handleEditSave = async () => {
    try {
      const { id, ...updateData } = editDialog.contact;
      await axios.put(`/api/harvest/contacts/${id}`, updateData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setMessage({ type: 'success', text: 'Contact updated successfully' });
      setEditDialog({ open: false, contact: null });
      loadContacts();
    } catch (error) {
      console.error('Error updating contact:', error);
      setMessage({ type: 'error', text: 'Failed to update contact' });
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      setExportError('');

      // Format dates to YYYY-MM-DD for the backend
      const formatDate = (date) => {
        if (!date) return null;
        return date.toISOString().split('T')[0];
      };

      const searchParams = new URLSearchParams({
        ...(filters.search && { search: filters.search }),
        ...(filters.date_from && { startDate: formatDate(filters.date_from) }),
        ...(filters.date_to && { endDate: formatDate(filters.date_to) }),
        ...(filters.location_id && { location_id: filters.location_id })
      });

      const token = localStorage.getItem('token');
      if (!token) {
        setExportError('Authentication required');
        return;
      }

      console.log('Exporting harvest contacts with params:', searchParams.toString());

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/harvest/contacts/export?${searchParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob'
        }
      );

      console.log('Export response:', response);

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'wcito-harvest.csv';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      } else {
        // Fallback: create filename with current timestamp
        const now = new Date();
        const timestamp = now.toISOString().replace(/[:.]/g, '-').split('T')[0] + '-' + 
                         now.toTimeString().split(' ')[0].replace(/:/g, '-');
        filename = `wcito-harvest-${timestamp}.csv`;
      }
      
      console.log('Downloading file:', filename);
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Export completed successfully');

    } catch (error) {
      console.error('Export error:', error);
      console.error('Error response:', error.response);
      setExportError(error.response?.data?.error || 'Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && contacts.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Harvest Contacts
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError('')}>
          {exportError}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Name, phone, or email"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Location</InputLabel>
              <Select
                value={filters.location_id}
                onChange={(e) => handleFilterChange('location_id', e.target.value)}
                label="Location"
              >
                <MenuItem value="">All Locations</MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From Date"
                value={filters.date_from}
                onChange={(date) => handleFilterChange('date_from', date)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="To Date"
                value={filters.date_to}
                onChange={(date) => handleFilterChange('date_to', date)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Box>

      {/* Export Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={isExporting ? <CircularProgress size={20} /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={isExporting}
          sx={{
            borderColor: '#DC2626',
            color: '#DC2626',
            '&:hover': {
              borderColor: '#B91C1C',
              backgroundColor: '#fef2f2',
            },
            px: 3,
            py: 1.5,
          }}
        >
          {isExporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </Box>

      {/* Contacts Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Evangelism Date</TableCell>
              <TableCell>Collector</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell>{contact.name}</TableCell>
                <TableCell>{contact.phone_number}</TableCell>
                <TableCell>{contact.email || '-'}</TableCell>
                <TableCell>
                  <Chip label={contact.location_name} size="small" />
                </TableCell>
                <TableCell>{formatDate(contact.evangelism_date)}</TableCell>
                <TableCell>
                  {contact.collector_name ? (
                    <Box>
                      <Typography variant="body2">{contact.collector_name}</Typography>
                      {contact.collector_phone && (
                        <Typography variant="caption" color="text.secondary">
                          {contact.collector_phone}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(contact)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(contact.id)} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, contact: null })} maxWidth="md" fullWidth>
        <DialogTitle>Edit Contact</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={editDialog.contact?.name || ''}
                onChange={(e) => setEditDialog(prev => ({
                  ...prev,
                  contact: { ...prev.contact, name: e.target.value }
                }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={editDialog.contact?.phone_number || ''}
                onChange={(e) => setEditDialog(prev => ({
                  ...prev,
                  contact: { ...prev.contact, phone_number: e.target.value }
                }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                value={editDialog.contact?.email || ''}
                onChange={(e) => setEditDialog(prev => ({
                  ...prev,
                  contact: { ...prev.contact, email: e.target.value }
                }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={editDialog.contact?.harvest_location_id || ''}
                  onChange={(e) => setEditDialog(prev => ({
                    ...prev,
                    contact: { ...prev.contact, harvest_location_id: e.target.value }
                  }))}
                  label="Location"
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Evangelism Date"
                  value={editDialog.contact?.evangelism_date ? new Date(editDialog.contact.evangelism_date) : null}
                  onChange={(date) => setEditDialog(prev => ({
                    ...prev,
                    contact: { ...prev.contact, evangelism_date: date }
                  }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Collector Name"
                value={editDialog.contact?.collector_name || ''}
                onChange={(e) => setEditDialog(prev => ({
                  ...prev,
                  contact: { ...prev.contact, collector_name: e.target.value }
                }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Collector Phone"
                value={editDialog.contact?.collector_phone || ''}
                onChange={(e) => setEditDialog(prev => ({
                  ...prev,
                  contact: { ...prev.contact, collector_phone: e.target.value }
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, contact: null })}>
            Cancel
          </Button>
          <Button onClick={handleEditSave} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default HarvestContacts;
